import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { Check, ChevronsUpDown, Download, FileDown, Search, Upload, Trash2, Paperclip, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, download, errMsg, inr, STATUS_COLORS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export function PageHeader({ title, subtitle, actions, testid }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 mb-6" data-testid={testid}>
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#1A2E2C]">{title}</h1>
        {subtitle && <p className="text-sm text-[#5C6B69] mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

export function StatusBadge({ value, testid }) {
  const cls = STATUS_COLORS[value] || "bg-slate-100 text-slate-600 border-slate-300";
  const label = String(value || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <span data-testid={testid} className={cn("inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border", cls)}>
      {label}
    </span>
  );
}

export function Money({ value, className, testid }) {
  return <span data-testid={testid} className={cn("num text-[#0D4F4A]", className)}>{inr(value)}</span>;
}

export function DemoBadge() {
  return <span className="ml-1 inline-flex px-1.5 py-0 rounded text-[10px] font-bold bg-[#C9A227]/15 text-[#8a6d14] border border-[#C9A227]/40">DEMO</span>;
}

export function DataTable({ columns, rows, loading, onRow, page, pages, total, onPage, testid, empty }) {
  return (
    <div className="bg-white border border-[#E5E0D8] rounded-xl shadow-sm overflow-hidden" data-testid={testid}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#FAF8F5] border-b border-[#E5E0D8]">
              {columns.map((c) => (
                <th key={c.key} className={cn("px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#5C6B69] text-left whitespace-nowrap", c.align === "right" && "text-right")}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={columns.length} className="px-4 py-12 text-center text-[#5C6B69]">
                <Loader2 className="inline h-5 w-5 animate-spin mr-2" />Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={columns.length} className="px-4 py-12 text-center text-[#5C6B69]">{empty || "No records found"}</td></tr>
            ) : rows.map((r, i) => (
              <tr key={r.id || i} onClick={onRow ? () => onRow(r) : undefined}
                className={cn("border-b border-[#E5E0D8]/60 hover:bg-[#FAF8F5]/70 transition-colors", onRow && "cursor-pointer")}
                data-testid={`${testid}-row-${i}`}>
                {columns.map((c) => (
                  <td key={c.key} className={cn("px-4 py-3 whitespace-nowrap", c.align === "right" && "text-right", c.className)}>
                    {c.render ? c.render(r) : (r[c.key] ?? "—")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-[#E5E0D8] bg-[#FAF8F5]/50">
          <span className="text-xs text-[#5C6B69]">{total} records · Page {page} of {pages}</span>
          <div className="flex gap-1.5">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid={`${testid}-prev`}>Prev</Button>
            <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid={`${testid}-next`}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function SearchBox({ value, onChange, placeholder, testid }) {
  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5C6B69]" />
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder || "Search…"}
        className="pl-9 bg-white border-[#E5E0D8]" data-testid={testid} />
    </div>
  );
}

export function Field({ label, children, required, hint }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold text-[#5C6B69] uppercase tracking-wide">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      {children}
      {hint && <p className="text-[11px] text-[#5C6B69]">{hint}</p>}
    </div>
  );
}

export function StateSelect({ value, onChange, testid }) {
  const [states, setStates] = useState([]);
  useEffect(() => { api.get("/settings/states").then((r) => setStates(r.data)).catch(() => {}); }, []);
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="bg-white border-[#E5E0D8]"><SelectValue placeholder="Select state" /></SelectTrigger>
      <SelectContent>
        {states.map((s) => <SelectItem key={s.code} value={s.name}>{s.name} ({s.code})</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export function EntityCombobox({ items, value, onChange, placeholder, testid, renderLabel }) {
  const [open, setOpen] = useState(false);
  const selected = items.find((i) => i.value === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" data-testid={testid}
          className="w-full justify-between bg-white border-[#E5E0D8] font-normal">
          <span className="truncate">{selected ? (renderLabel ? renderLabel(selected) : selected.label) : (placeholder || "Select…")}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Type to search…" />
          <CommandList>
            <CommandEmpty>No match found.</CommandEmpty>
            <CommandGroup>
              {items.map((i) => (
                <CommandItem key={i.value} value={`${i.label} ${i.sub || ""}`}
                  onSelect={() => { onChange(i.value); setOpen(false); }}
                  data-testid={`${testid}-option-${i.value}`}>
                  <Check className={cn("mr-2 h-4 w-4", value === i.value ? "opacity-100" : "opacity-0")} />
                  <div className="min-w-0">
                    <div className="truncate font-medium">{i.label}</div>
                    {i.sub && <div className="text-xs text-[#5C6B69] truncate">{i.sub}</div>}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function ConfirmDialog({ open, onClose, onConfirm, title, description, destructive = true, testid }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid={testid}>
        <DialogHeader>
          <DialogTitle className="font-display">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)} data-testid={`${testid}-cancel`}>Cancel</Button>
          <Button variant={destructive ? "destructive" : "default"} onClick={onConfirm} data-testid={`${testid}-confirm`}>Confirm</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const PRESETS = [
  { key: "today", label: "Today", range: () => [dayjs(), dayjs()] },
  { key: "week", label: "This Week", range: () => [dayjs().startOf("week"), dayjs()] },
  { key: "month", label: "This Month", range: () => [dayjs().startOf("month"), dayjs()] },
  { key: "prev_month", label: "Previous Month", range: () => [dayjs().subtract(1, "month").startOf("month"), dayjs().subtract(1, "month").endOf("month")] },
  { key: "quarter", label: "This Quarter", range: () => {
      const m = dayjs().month();
      const qStart = dayjs().month(Math.floor(m / 3) * 3).startOf("month");
      return [qStart, dayjs()];
    } },
  { key: "fy", label: "Financial Year", range: () => {
      const now = dayjs();
      const start = now.month() >= 3 ? now.month(3).date(1) : now.subtract(1, "year").month(3).date(1);
      return [start, start.add(1, "year").subtract(1, "day")];
    } },
];

export function DateRangeFilter({ from, to, onChange, testid }) {
  const [preset, setPreset] = useState("month");
  const apply = (key) => {
    setPreset(key);
    if (key === "custom") return;
    const p = PRESETS.find((x) => x.key === key);
    if (p) {
      const [f, t] = p.range();
      onChange(f.format("YYYY-MM-DD"), t.format("YYYY-MM-DD"));
    }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={testid}>
      <Select value={preset} onValueChange={apply}>
        <SelectTrigger className="w-40 bg-white border-[#E5E0D8]" data-testid={`${testid}-preset`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PRESETS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
          <SelectItem value="custom">Custom Range</SelectItem>
        </SelectContent>
      </Select>
      <Input type="date" value={from} onChange={(e) => { setPreset("custom"); onChange(e.target.value, to); }}
        className="w-36 bg-white border-[#E5E0D8]" data-testid={`${testid}-from`} />
      <span className="text-xs text-[#5C6B69]">to</span>
      <Input type="date" value={to} onChange={(e) => { setPreset("custom"); onChange(from, e.target.value); }}
        className="w-36 bg-white border-[#E5E0D8]" data-testid={`${testid}-to`} />
    </div>
  );
}

export function ExportButtons({ base, params, testid }) {
  const qs = new URLSearchParams(Object.entries(params || {}).filter(([, v]) => v)).toString();
  return (
    <div className="flex gap-1.5">
      <Button size="sm" variant="outline" data-testid={`${testid}-csv`}
        onClick={() => download(`${base}?${qs}&format=csv`).catch((e) => toast.error(errMsg(e)))}>
        <FileDown className="h-3.5 w-3.5 mr-1" />CSV
      </Button>
      <Button size="sm" variant="outline" data-testid={`${testid}-xlsx`}
        onClick={() => download(`${base}?${qs}&format=xlsx`).catch((e) => toast.error(errMsg(e)))}>
        <Download className="h-3.5 w-3.5 mr-1" />XLSX
      </Button>
    </div>
  );
}

export function AttachmentPanel({ entityType, entityId, testid }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => {
    if (!entityId) return;
    api.get(`/files?entity_type=${entityType}&entity_id=${entityId}`)
      .then((r) => setFiles(r.data.files)).catch(() => {});
  };
  useEffect(load, [entityType, entityId]);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("entity_type", entityType);
      fd.append("entity_id", entityId);
      fd.append("file", file);
      await api.post("/files/upload", fd);
      toast.success("File uploaded");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <div data-testid={testid} className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-[#5C6B69] flex items-center gap-1">
          <Paperclip className="h-3.5 w-3.5" /> Attachments ({files.length})
        </span>
        <label className="cursor-pointer">
          <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={upload}
            data-testid={`${testid}-input`} />
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#0D4F4A] hover:underline">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload
          </span>
        </label>
      </div>
      {files.length === 0 ? (
        <p className="text-xs text-[#5C6B69] italic">No documents attached (PDF/JPG/PNG/WebP, max 10 MB)</p>
      ) : (
        <ul className="space-y-1">
          {files.map((f) => (
            <li key={f.id} className="flex items-center justify-between text-xs bg-[#FAF8F5] border border-[#E5E0D8] rounded-lg px-3 py-2"
              data-testid={`${testid}-file-${f.id}`}>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${f.id}/download`} target="_blank"
                rel="noreferrer" className="text-[#0D4F4A] font-medium hover:underline truncate">
                {f.original_filename}
              </a>
              <button className="text-red-500 hover:text-red-700 ml-2"
                onClick={async () => { await api.delete(`/files/${f.id}`); load(); }}
                data-testid={`${testid}-delete-${f.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
